import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  allowedStatusChangeReasons,
  ProfessionalStatus,
  ProfessionalStatusChangeReason,
  professionalStatusChangeReasonLabels,
  professionalStatusLabels,
} from "../schema/professional-schema";
import { useChangeProfessionalStatus } from "../hooks/use-change-professional-status";

const NOTE_MAX = 1000;

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  professionalId: string;
  professionalName: string;
  currentStatus: ProfessionalStatus;
  onChanged?: (newStatus: ProfessionalStatus) => void;
};

// The only way to change a professional's status in the CRM (RH-506): pick the
// new status, a reason that applies to it, and an optional note. The server
// records all of it alongside the change.
export default function ChangeStatusDialog({
  open,
  onOpenChange,
  professionalId,
  professionalName,
  currentStatus,
  onChanged,
}: Props) {
  const [status, setStatus] = useState<ProfessionalStatus | "">("");
  const [reason, setReason] = useState<ProfessionalStatusChangeReason | "">("");
  const [note, setNote] = useState("");
  const { mutate, isPending } = useChangeProfessionalStatus(professionalId);

  const reasons = useMemo(
    () => (status ? allowedStatusChangeReasons[status] : []),
    [status]
  );

  const reset = () => {
    setStatus("");
    setReason("");
    setNote("");
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleStatusChange = (value: string) => {
    const next = value as ProfessionalStatus;
    setStatus(next);
    // A reason picked for one status may not apply to the next one.
    if (reason && !allowedStatusChangeReasons[next].includes(reason)) {
      setReason("");
    }
  };

  const canSave =
    !!status && status !== currentStatus && !!reason && !isPending;

  const handleSave = () => {
    if (!status || !reason) return;
    mutate(
      { status, reason, note },
      {
        onSuccess: () => {
          onChanged?.(status);
          handleOpenChange(false);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change status</DialogTitle>
          <DialogDescription>
            {professionalName} is currently{" "}
            <span className="font-medium text-foreground">
              {professionalStatusLabels[currentStatus] ?? currentStatus}
            </span>
            . This change is recorded in the status history.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="new-status">New status</Label>
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger id="new-status" className="shadow-none">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(professionalStatusLabels).map(
                  ([value, label]) => (
                    <SelectItem
                      key={value}
                      value={value}
                      disabled={value === currentStatus}
                    >
                      {label}
                      {value === currentStatus ? " (current)" : ""}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="status-reason">Reason</Label>
            <Select
              value={reason}
              onValueChange={(v) => setReason(v as ProfessionalStatusChangeReason)}
              disabled={!status}
            >
              <SelectTrigger id="status-reason" className="shadow-none">
                <SelectValue
                  placeholder={status ? "Select reason" : "Pick a status first"}
                />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((r) => (
                  <SelectItem key={r} value={r}>
                    {professionalStatusChangeReasonLabels[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="status-note">
              Note{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <Textarea
              id="status-note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
              placeholder="Anything the next person reading this history should know"
              rows={3}
              className="shadow-none resize-none"
            />
            <p className="text-right text-[11px] text-muted-foreground">
              {note.length}/{NOTE_MAX}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isPending ? "Saving…" : "Save change"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
